import { useFeatureToggle } from '~/composables/useFeatureToggle'
import {
  BOOK_NOW_COOKIE,
  parseBookNowCookie,
  resolveItemCategory4,
  resolveQuickCheckout,
  variantToBookNowCookie,
  type BookNowVariant,
  type QuickCheckoutContext,
} from '~/utils/bookNowCheckout'

/**
 * "Book Now" quick-checkout A/B test (KFT-3914).
 *
 * In the `variation` bucket a simple event booking is added to the cart and the
 * user is routed straight to `/checkout`, skipping the cart sidebar. `control`
 * keeps today's add-to-cart + sidebar behaviour.
 *
 * The bucket is set at runtime by the VWO "Book Now" campaign through the window
 * bridge (see `useBookNowExperimentBridge`). Two layers keep it correct:
 *
 * - **In-memory `useState`** is the reactive source the app reads, so if VWO
 *   fires `setVariant()` *after* the buy box has already mounted (as default),
 *   the app reacts and switches to Book Now — no timing risk.
 * - **`bookNow` cookie** is the persistence fallback: it seeds the in-memory
 *   state on load so a hard navigation (product cards open event pages in a new
 *   tab) restores the bucket immediately and it's readable at SSR time. When the
 *   cookie is absent the visitor is not bucketed and behaviour is unchanged
 *   (add-to-cart, no `item_category4` tag).
 *
 * `setVariant()` writes both, so the cookie always matches what VWO assigned.
 *
 * The cookie is safe for the CDN: CloudFront's cache key excludes cookies, and
 * the variant produces no visual/DOM difference (same CTA, same markup) — only
 * click behaviour and the GA tag differ — so SSR reading it never poisons the
 * shared cached HTML.
 *
 * Pure decision logic lives in `~/utils/bookNowCheckout`.
 */

const BOOK_NOW_STATE_KEY = 'experiment:book-now'
/** Persist for 30 days so the bucket sticks across the visit. */
const BOOK_NOW_COOKIE_MAX_AGE = 60 * 60 * 24 * 30
/** Local QA override so the variation can be exercised without VWO (prod-only). */
const BOOK_NOW_FEATURE_TOGGLE = 'book-now'

declare global {
  interface Window {
    kftExperiments?: {
      bookNow?: {
        setVariant: (variant: string) => void
        getVariant: () => BookNowVariant | null
      }
      [key: string]: unknown
    }
  }
}

export const useBookNowExperiment = () => {
  const cookie = useCookie<string | null>(BOOK_NOW_COOKIE, {
    path: '/',
    sameSite: 'lax',
    maxAge: BOOK_NOW_COOKIE_MAX_AGE,
    // Store the raw value ('true'/'false') instead of Nuxt's default JSON
    // encoding, so the cookie is plainly readable ('bookNow=true').
    encode: (value) => value,
    decode: (value) => value,
  })
  const { isFeatureEnabled } = useFeatureToggle()

  // In-memory reactive source, seeded from the persisted cookie so a hard
  // navigation restores the bucket immediately, while still reacting to a later
  // VWO setVariant() call on the same page.
  const variant = useState<BookNowVariant | null>(BOOK_NOW_STATE_KEY, () =>
    parseBookNowCookie(cookie.value)
  )

  // The QA feature toggle forces the variation locally, where VWO can't run.
  const effectiveVariant = computed<BookNowVariant | null>(() =>
    isFeatureEnabled(BOOK_NOW_FEATURE_TOGGLE) ? 'variation' : variant.value
  )

  const isVariation = computed(() => effectiveVariant.value === 'variation')

  const itemCategory4 = computed(() =>
    resolveItemCategory4(effectiveVariant.value)
  )

  const setVariant = (value: unknown): void => {
    if (value === 'control' || value === 'variation') {
      // In-memory first so the app reacts immediately even after mount, then
      // persist to the cookie so it survives navigation and matches VWO.
      variant.value = value
      cookie.value = variantToBookNowCookie(value)
    }
  }

  const shouldQuickCheckout = (ctx: QuickCheckoutContext = {}): boolean =>
    resolveQuickCheckout(isVariation.value, ctx)

  return {
    variant,
    isVariation,
    itemCategory4,
    setVariant,
    shouldQuickCheckout,
  }
}

/**
 * Exposes `window.kftExperiments.bookNow.setVariant('control' | 'variation')` so
 * the VWO "Book Now" campaign can assign the bucket from its variation JS.
 * Must be called once from a setup context (app.vue).
 */
export const useBookNowExperimentBridge = (): void => {
  const { setVariant, variant } = useBookNowExperiment()

  onMounted(() => {
    if (typeof window === 'undefined') return
    window.kftExperiments = window.kftExperiments || {}
    window.kftExperiments.bookNow = {
      setVariant,
      getVariant: () => variant.value,
    }
  })
}
